import React, { useState, useEffect, useCallback } from 'react';
import { qrcodeAPI, categoriesAPI } from '../services/api';
import { FiPrinter, FiPlus, FiTrash2, FiCheckCircle, FiAlertCircle, FiLoader, FiExternalLink, FiRefreshCw, FiDownload } from 'react-icons/fi';

const QRGeneratorPage = () => {
  const [quantity, setQuantity] = useState(10);
  const [categorieId, setCategorieId] = useState('');
  const [categories, setCategories] = useState([]);
  const [blanks, setBlanks] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  const fetchCategories = useCallback(async () => {
    try {
      const res = await categoriesAPI.getAll();
      setCategories(res.data.data || []);
    } catch (err) { console.error('Erreur chargement categories:', err); }
  }, []);

  const fetchBlanks = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await qrcodeAPI.getBlanks({ page, limit: 50 });
      setBlanks(res.data.data || []);
      setPagination(res.data.pagination || { page: 1, pages: 1, total: 0 });
    } catch (err) { console.error('Erreur chargement QR vierges:', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCategories(); fetchBlanks(); }, [fetchCategories, fetchBlanks]);

  const handleGenerate = async () => {
    if (quantity < 1 || quantity > 200) { setMessage({ type: 'error', text: 'La quantite doit etre entre 1 et 200' }); return; }
    setGenerating(true); setMessage(null);
    try {
      const res = await qrcodeAPI.generateBlank({ quantity: parseInt(quantity), categorie_id: categorieId || undefined });
      const count = res.data.count || res.data.data?.length || quantity;
      setMessage({ type: 'success', text: count + ' QR codes vierges generes avec succes !' });
      fetchBlanks();
    } catch (err) { setMessage({ type: 'error', text: err.response?.data?.error || 'Erreur lors de la generation' }); }
    finally { setGenerating(false); }
  };

  const handleSelectAll = () => {
    if (selectedIds.length === blanks.length) { setSelectedIds([]); }
    else { setSelectedIds(blanks.map(b => b.id)); }
  };

  const handleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handlePrintLabels = async () => {
    const ids = selectedIds.length > 0 ? selectedIds : [];
    if (ids.length === 0) { setMessage({ type: 'error', text: 'Selectionnez au moins un QR code' }); return; }
    try {
      const url = await qrcodeAPI.batchLabelsLink(ids);
      window.open(url, '_blank');
    } catch (e) {
      setMessage({ type: 'error', text: 'Erreur lors de la generation du lien' });
    }
  };

  const handleExportPDF = async () => {
    if (selectedIds.length === 0) {
      setMessage({ type: 'error', text: 'Selectionnez au moins un QR code' });
      return;
    }
    try {
      const url = await qrcodeAPI.batchPdfLink(selectedIds);
      window.open(url, '_blank');
    } catch (e) {
      setMessage({ type: 'error', text: 'Erreur lors de la generation du lien' });
    }
  };

  const handlePrintOne = async (id) => {
    try {
      const url = await qrcodeAPI.batchLabelsLink([id]);
      window.open(url, '_blank');
    } catch (e) {
      setMessage({ type: 'error', text: 'Erreur lors de la generation du lien' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce QR code vierge ?')) return;
    try {
      await qrcodeAPI.deleteBlank(id);
      setMessage({ type: 'success', text: 'QR code supprime' });
      fetchBlanks();
      setSelectedIds(prev => prev.filter(x => x !== id));
    } catch (err) { setMessage({ type: 'error', text: err.response?.data?.error || 'Erreur suppression' }); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Generateur de QR Codes</h1>
          <p style={{ color: 'var(--text-secondary)', marginTop: '0.25rem', fontSize: '0.9rem' }}>Generez des etiquettes QR vierges pour vos immobilisations</p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header" style={{ borderBottom: '2px solid var(--primary-color)' }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}><FiPlus /> Generer des QR Codes vierges</h3>
        </div>
        <div className="card-body" style={{ padding: '1.5rem' }}>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: '0 0 150px' }}>
              <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Quantite</label>
              <input type="number" className="form-control" value={quantity} onChange={e => setQuantity(e.target.value)} min="1" max="200" style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1px solid var(--border-color)', borderRadius: '6px', fontSize: '1rem' }} />
            </div>
            <div style={{ flex: '1', minWidth: '200px' }}>
              <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Categorie (optionnel)</label>
              <select className="form-control" value={categorieId} onChange={e => setCategorieId(e.target.value)} style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1px solid var(--border-color)', borderRadius: '6px', fontSize: '1rem', background: 'white' }}>
                <option value="">-- Toutes categories --</option>
                {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.nom}</option>)}
              </select>
            </div>
            <button className="btn btn-primary" onClick={handleGenerate} disabled={generating} style={{ padding: '0.6rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
              {generating ? <FiLoader className="spin" /> : <FiPlus />}
              {generating ? 'Generation...' : 'Generer'}
            </button>
          </div>

          {message && (
            <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '0.5rem', background: message.type === 'success' ? '#d4edda' : '#f8d7da', color: message.type === 'success' ? '#155724' : '#721c24', border: '1px solid ' + (message.type === 'success' ? '#c3e6cb' : '#f5c6cb') }}>
              {message.type === 'success' ? <FiCheckCircle /> : <FiAlertCircle />}
              {message.text}
            </div>
          )}

          <div style={{ marginTop: '1rem', padding: '1rem', background: 'var(--bg-secondary, #f8f9fa)', borderRadius: '8px', fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
            <strong>Mode d emploi :</strong><br/>
            1. Choisir la quantite de QR codes a generer<br/>
            2. Cliquer sur Generer pour creer les enregistrements vierges<br/>
            3. Selectionner les QR codes et cliquer sur Imprimer les etiquettes<br/>
            4. Imprimer a l imprimerie les etiquettes avec QR code et logo OGP<br/>
            5. Scanner chaque QR code pour remplir le formulaire d enrolement<br/>
            6. Coller l etiquette sur l immobilisation physique
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header" style={{ borderBottom: '2px solid var(--primary-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
          <h3 style={{ margin: 0 }}>QR Codes vierges ({pagination.total})</h3>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button className="btn btn-ghost" onClick={() => fetchBlanks(pagination.page)} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.85rem' }}><FiRefreshCw /> Actualiser</button>
            {blanks.length > 0 && (<>
              <button className="btn btn-ghost" onClick={handleSelectAll} style={{ fontSize: '0.85rem' }}>{selectedIds.length === blanks.length ? 'Deselectionner tout' : 'Tout selectionner'}</button>
              <button className="btn btn-primary" onClick={handlePrintLabels} disabled={selectedIds.length === 0} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.85rem', opacity: selectedIds.length === 0 ? 0.5 : 1 }}><FiPrinter /> Imprimer ({selectedIds.length})</button>
                <button
                  className="btn btn-primary"
                  onClick={handleExportPDF}
                  disabled={selectedIds.length === 0}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.3rem',
                    fontSize: '0.85rem',
                    opacity: selectedIds.length === 0 ? 0.5 : 1,
                    background: '#9B7B2E',
                  }}
                >
                  <FiDownload />
                  PDF ({selectedIds.length})
                </button>
            </>)}
          </div>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {loading ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-secondary)' }}><FiLoader className="spin" style={{ fontSize: '2rem', marginBottom: '0.5rem' }} /><p>Chargement...</p></div>
          ) : blanks.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-secondary)' }}><p style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>Aucun QR code vierge</p><p style={{ fontSize: '0.85rem' }}>Utilisez le formulaire ci-dessus pour generer des QR codes</p></div>
          ) : (<>
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table" style={{ width: '100%' }}>
                <thead><tr>
                  <th style={{ width: '40px', textAlign: 'center' }}><input type="checkbox" checked={selectedIds.length === blanks.length && blanks.length > 0} onChange={handleSelectAll} /></th>
                  <th>N Identification</th>
                  <th>Statut</th>
                  <th>Date de creation</th>
                  <th style={{ width: '120px', textAlign: 'center' }}>Actions</th>
                </tr></thead>
                <tbody>
                  {blanks.map(item => (
                    <tr key={item.id}>
                      <td style={{ textAlign: 'center' }}><input type="checkbox" checked={selectedIds.includes(item.id)} onChange={() => handleSelect(item.id)} /></td>
                      <td><span style={{ fontWeight: 700, color: 'var(--primary-color)', fontFamily: 'monospace', fontSize: '0.95rem' }}>{item.numero_identification}</span></td>
                      <td><span style={{ padding: '0.25rem 0.75rem', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 600, background: '#fff3cd', color: '#856404', border: '1px solid #ffc107' }}>En attente</span></td>
                      <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{formatDate(item.created_at)}</td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '0.3rem', justifyContent: 'center' }}>
                          <button className="btn btn-ghost" onClick={() => handlePrintOne(item.id)} title="Imprimer etiquette" style={{ padding: '0.3rem 0.5rem', fontSize: '0.85rem' }}><FiPrinter /></button>
                          <button className="btn btn-ghost" onClick={() => window.open('/enroll/' + item.id, '_blank')} title="Voir page enrolement" style={{ padding: '0.3rem 0.5rem', fontSize: '0.85rem' }}><FiExternalLink /></button>
                          <button className="btn btn-ghost" onClick={() => handleDelete(item.id)} title="Supprimer" style={{ padding: '0.3rem 0.5rem', fontSize: '0.85rem', color: '#dc3545' }}><FiTrash2 /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pagination.pages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', padding: '1rem', borderTop: '1px solid var(--border-color)' }}>
                {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(p => (
                  <button key={p} className={'btn ' + (p === pagination.page ? 'btn-primary' : 'btn-ghost')} onClick={() => fetchBlanks(p)} style={{ padding: '0.3rem 0.7rem', fontSize: '0.85rem', minWidth: '35px' }}>{p}</button>
                ))}
              </div>
            )}
          </>)}
        </div>
      </div>

      <style>{'  .spin { animation: spin 1s linear infinite; } @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }'}</style>
    </div>
  );
};

export default QRGeneratorPage;
